<script setup lang="ts">
import { ref } from 'vue';
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import ChevronDownIcon from '@primeicons/vue/chevron-down';
import ChevronUpIcon from '@primeicons/vue/chevron-up';
import { Stack, Typography } from '@apparatix/library/core';
import { Noir } from '@apparatix/theme/Noir';

type Props = {
  title: string;
  itemCount: number;
  badgeSeverity: 'primary' | 'danger' | 'success';
  showLoadMoreButton?: boolean;
  loadMoreDisabled?: boolean;
  loadMoreButtonLabel?: string;
  uuid?: string;
}

type Emits = {
  (e: 'show-more', uuid: string): void;
}

const props = defineProps<Props>();

const emit = defineEmits<Emits>();

const collapsed = ref(props.itemCount === 0 ? true : false);

const onShowMoreClick = () => {
  if (props.uuid) {
    emit('show-more', props.uuid);
  }
};
</script>
<template>
  <Stack>
    <div class="flex items-center gap-2">
      <div class="grow-0">
        <Typography variant="activity-heading">
          {{ props.title }}
        </Typography>
      </div>
      <div class="grow-0">
        <Badge :severity="props.badgeSeverity" :dt="Noir.badge">
          {{ props.itemCount }}
        </Badge>
      </div>
      <div class="grow h-[1px] bg-[#E2E8F0]" />
      <div class="grow-0">
        <Button
          v-if="props.showLoadMoreButton"
          :dt="Noir.button"
          :disabled="props.loadMoreDisabled"
          severity="secondary"
          variant="outlined"
          class="mx-2"
          size="small"
          @click="onShowMoreClick"
        >
          {{ props.loadMoreButtonLabel }}
        </Button>
        <Button
          icon-only
          :dt="Noir.button"
          rounded
          variant="outlined"
          :aria-label="collapsed ? 'Expand' : 'Collapse'"
          @click="collapsed = !collapsed"
        >
          <ChevronDownIcon v-if="collapsed" />
          <ChevronUpIcon v-else />
        </Button>
      </div>
    </div>
    <Stack v-if="!collapsed" :gap="6">
      <slot />
    </Stack>
  </Stack>
</template>
